import { NameValue } from '@flyfreely-portal-ui/flyfreely';
import { GraphQlPaginationService } from 'libs/graph-ql/src/lib/interfaces';

/**
 * This represents a column configuration, used in the available column
 * list.
 * @type R the type of the row
 */
export interface TableColumn<R = any> {
    /**
     * This is the identifier used to refer to this column. if not set it will
     * default to `value`.
     */
    key?: string;
    /**
     * The property to get the value from
     */
    value?: string;
    /**
     * The value that is always displayed for this column
     */
    staticValue?: string;
    /**
     * The title of the column
     */
    name: string;
    /**
     * The description of the column for showing in the tooltip
     */
    description?: string;
    /**
     * Which search option is provided
     */
    searchable: SearchOptions;
    /**
     * The options to show when searchable = 'selection'
     */
    searchOptions?: readonly NameValue<any>[];

    /**
     * A search function factory that produces a search function for
     * the given selection.
     * The search function gets the column's value, and the entire row
     * in case it needs to reference other properties.
     * @param selection the value that is entered in the search box
     * @param value the raw value of the column (no the formatted value)
     * @param row the entire row
     */
    searchFunction?: (selection: any) => (value: any, row: R) => boolean;
    /**
     * the default search value
     */
    defaultSearch?: string;
    /**
     * A placeholder for the search element
     */
    searchPlaceholder?: string;
    /**
     * Should the search item be clearable? Currently only applies to selectable searches.
     */
    clearableSearch?: boolean;
    /**
     * The property to group items by when searchable = 'selection'. Passed to ng-select's groupBy.
     */
    groupBy?: string;
    /**
     * Can the visibility of the column be toggled
     */
    selectable: boolean;
    /**
     * Is the column selected by default
     */
    defaultSelection: boolean;
    /**
     * Can this column affect the table sorting
     */
    sortable?: boolean;
    /**
     * The function to call when the user clicks on a link
     */
    linkFunction?: (row: R) => void;
    /**
     * A formatter to convert the raw value into a string for display
     */
    formatter?: string;
    formatterFunction?: (val: any, row: R) => string | null | undefined;
    /**
     * Provide a sorting function, otherwise the rendered text will be sorted.
     */
    compareFunction?: (val1: CompareData, val2: CompareData) => number;

    /**
     * A typed service that handles data fetching, formatting and pagination for async-selection columns
     * Required if the column's searchable = 'async-selection
     */
    asyncSelectService?: GraphQlPaginationService;
}

/**
 * A group of columns that are toggled on/off together via a single entry
 * in the column selector popover.
 */
export interface TableColumnGroup {
    /**
     * The authoritative identifier for this group. Stored in selectedColumns
     * when the group is toggled on.
     */
    key: string;
    /**
     * The label shown in the column selector popover for this group.
     */
    name: string;
    /**
     * Whether this group appears in the column selector popover.
     */
    selectable: boolean;
    /**
     * Whether this group's columns are shown by default.
     */
    defaultSelection: boolean;
    /**
     * The individual columns that belong to this group.
     * Each column should have `selectable: false` so they do not
     * appear individually in the popover.
     */
    columns: TableColumn[];
}

/**
 * Union type for entries in the availableColumns input.
 * Either a single column or a named group of columns.
 */
export type AvailableColumn = TableColumn | TableColumnGroup;

/**
 * Type guard to distinguish a TableColumnGroup from a TableColumn.
 */
export function isTableColumnGroup(
    col: AvailableColumn
): col is TableColumnGroup {
    return 'columns' in col;
}

export interface CompareData {
    rawData: any;
    renderedData: string;
}

export interface TableAction<R = any> {
    action: (data: R) => void;
    icon: string;
    tooltip: string;
    disabled?: (data: R) => boolean;
}

export interface BulkTableAction<R = any> {
    action: (data: R[]) => Promise<void> | void;
    icon?: string;
    tooltip?: string;
    name: string;
}

/**
 * The configuration for the table. "totalItems" also serves as a flag that this is a GraphQL table.
 * In GraphQL mode, the pagination for the table returns the currentPage via the "pageChanged" event emitter.
 * @param limit The amount of items to show per page
 * @param actions Any table action buttons to display in the right-most column
 * @param limitSelection An array of options for page sizes
 * @param totalItems The total number of items in the list. Having this as not null will set the table in GraphQL mode.
 * @param currentPage REQUIRED IF totalItems IS NOT NULL. This allows a parent to manage the table's pagination.
 * @param isSeenFunction Pass a boolean function that returns true if the data row is in a "seen" state. Returns true by default.
 */
export interface TableConfig<R = any> {
    limit: number;
    actions: TableAction<R>[];
    bulkActions?: BulkTableAction<R>[];
    limitSelection?: number[];
    totalItems?: number;
    currentPage?: number;
    /**
     * The timezone to use for calculating start/end of day timestamps for date filters. Required if you have date range filters.
     */
    timeZone?: string;
    /**
     * Is pagination, searching, and sorting done server side? If so, disable local. Named to match ngx-easy-table.
     */
    serverPagination?: boolean;
    /**
     * A function that takes a row and returns true if the row is in a "seen" state
     */
    isSeenFunction?: (data: R) => boolean;
    /**
     * A function that takes a row and returns a boolean indicating if the row is in a "warning" state
     */
    isWarningFunction?: (data: R) => boolean;
}

export interface ColumnSort {
    key: string;
    dir: 'asc' | 'desc';
}

export interface ColumnSortPreferences {
    column: string;
    ascending: boolean;
}

export type SearchOptions =
    | boolean
    | 'text'
    | 'date'
    | 'daterange'
    | 'selection'
    | 'async-selection';

export type SearchValue = string | number | string[];

export interface TableSearchValues {
    [columnKey: string]: SearchValue;
}

/**
 * This interface describes the shape of the user preferences used
 * to store the table setup.
 */
export interface TableSetupUserPreferences {
    selectedColumns?: string[];
    columnSorting?: ColumnSortPreferences;
    tableSearch?: TableSearchValues;
    itemLimit?: number;
}
